import BottomSheet, { BottomSheetBackdrop, BottomSheetScrollView, BottomSheetTextInput } from '@gorhom/bottom-sheet';
import { useCallback, useMemo, useRef, useState } from 'react';
import { StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { colors, spacing } from '@/constants/theme';
import { useFormSheet } from '@/hooks/useFormSheet';
import { isValidEmail } from '@/lib/contact';
import { isApiError } from '@/services/errors';
import { usersService } from '@/services/users';
import { authStore } from '@/stores/auth.store';

import { Button } from './Button';
import { Heading } from './Heading';
import { Input } from './Input';
import { Text } from './Text';

export interface EditEmailSheetProps {
  onClose: () => void;
}

// Same sheet as EditNameSheet (fixed snap points + fillParent keyboard
// behavior; see the notes there). The server owns everything after the save:
// it keeps the old address for tracking links, renames the AC contact, and
// refuses an address on another account with a support message we show as is.
export function EditEmailSheet({ onClose }: EditEmailSheetProps) {
  const snapPoints = useMemo<(string | number)[]>(() => ['65%', '95%'], []);
  const { sheetRef, expandOnFocus, handleChange, close } = useFormSheet(onClose, snapPoints.length - 1);
  const { top: topInset } = useSafeAreaInsets();

  const current = authStore.user?.email ?? '';
  const [email, setEmail] = useState(current);
  const [isSaving, setIsSaving] = useState(false);
  // Ref, not state: the return key and a tap on Save can both fire before the
  // re-render that disables Save, and each request spends one of the user's
  // 3 weekly email changes.
  const inFlight = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [focused, setFocused] = useState(false);

  const renderBackdrop = useCallback(
    (props: React.ComponentProps<typeof BottomSheetBackdrop>) => (
      <BottomSheetBackdrop {...props} appearsOnIndex={0} disappearsOnIndex={-1} />
    ),
    [],
  );

  const trimmed = email.trim();
  const canSave = isValidEmail(trimmed) && trimmed.toLowerCase() !== current.toLowerCase();

  const handleChangeText = (value: string) => {
    setEmail(value);
    if (error) setError(null);
  };

  const handleSave = async () => {
    if (!canSave || inFlight.current) return;
    inFlight.current = true;
    setIsSaving(true);
    setError(null);
    try {
      const saved = await usersService.changeEmail(trimmed);
      // Saved: show it now and close. The refresh is best-effort; failing it
      // must not read as a failed change and invite a retry.
      authStore.applySavedEmail(saved);
      void authStore.refreshMe().catch(() => undefined);
      close();
    } catch (err) {
      setError(messageForChange(err));
    } finally {
      inFlight.current = false;
      setIsSaving(false);
    }
  };

  return (
    <BottomSheet
      ref={sheetRef}
      index={0}
      snapPoints={snapPoints}
      enableDynamicSizing={false}
      enablePanDownToClose={!isSaving}
      topInset={topInset}
      backdropComponent={renderBackdrop}
      onChange={handleChange}
      handleIndicatorStyle={styles.handle}
      backgroundStyle={styles.background}
      keyboardBehavior="fillParent"
      keyboardBlurBehavior="restore"
      android_keyboardInputMode="adjustResize"
    >
      <BottomSheetScrollView
        contentContainerStyle={styles.content}
        keyboardShouldPersistTaps="handled"
        testID="edit-email-sheet"
      >
        <Heading size="2xl">Change email</Heading>

        {error && (
          <Text variant="caption" color="danger" accessibilityRole="alert" testID="edit-email-error">
            {error}
          </Text>
        )}

        <Input
          label="Email"
          value={email}
          onChangeText={handleChangeText}
          keyboardType="email-address"
          autoComplete="email"
          textContentType="emailAddress"
          autoCapitalize="none"
          autoCorrect={false}
          returnKeyType="done"
          onSubmitEditing={() => void handleSave()}
          onFocus={() => {
            setFocused(true);
            expandOnFocus();
          }}
          onBlur={() => setFocused(false)}
          focused={focused}
          textInputComponent={BottomSheetTextInput}
          testID="input-email"
        />

        <Text variant="caption" color="textMuted">
          We'll send your cash-outs, login codes, and updates here.
        </Text>

        <Button
          label="Save"
          variant="primary"
          fullWidth
          loading={isSaving}
          disabled={!canSave}
          onPress={handleSave}
          testID="save-email-btn"
        />
      </BottomSheetScrollView>
    </BottomSheet>
  );
}

const styles = StyleSheet.create({
  handle: {
    backgroundColor: colors.border,
  },
  background: {
    backgroundColor: colors.surface,
  },
  content: {
    padding: spacing.lg,
    gap: spacing.base,
  },
});

const FALLBACK_ERROR = 'Could not save. Please try again.';

// The server's own words for the cases it explains (409 taken, 422 invalid,
// 429 weekly limit). Anything else, like a 5xx whose message is just
// "Request failed (503)" or a dropped connection, gets the plain fallback.
function messageForChange(err: unknown): string {
  if (isApiError(err) && err.status >= 400 && err.status < 500 && err.message) return err.message;
  return FALLBACK_ERROR;
}
